import * as React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Modal, Button, Descriptions, Select, Input, Icon, message, Checkbox, Alert } from 'antd'
import { inject, observer } from 'mobx-react'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import {
  API,
  CreatePayment,
  IBaseProduct,
  IPayment,
  IBatchResponseWithStatus,
  PaymentStatus,
  PaymentTypeIds,
  CreateTransaction,
  TransactionStatus,
  TaxGroup,
} from '@getgreenline/homi-shared'
import { SellableProductAsyncInput } from '../../../../components/ProductAsyncInput/SellableProductAsyncInput'
import { CurrencyInput } from '../../../../components/CurrencyInput'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { Receipt } from '../../../../components/Receipt/Receipt'
import { Currency } from '../../../../components/Currency'
import { taxApi, TaxModels } from '@getgreenline/products'
import { isEmpty } from 'lodash'

interface Props {
  onClose: () => void
  onSubmit: () => void
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  selectedProduct?: IBaseProduct
  batches: IBatchResponseWithStatus[]
  selectedBatch?: IBatchResponseWithStatus
  isSaving: boolean
  overrideTaxes: boolean
  selectedTaxGroup?: TaxGroup
  selectedPaymentTypeId?: number
  createdPayment?: IPayment
  mappedTaxes: Map<number, TaxModels.ITaxContract>
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ManualPaymentModal extends React.Component<Props, State> {
  createPayment: CreatePayment

  constructor(props: Props) {
    super(props)
    const company = this.props.currentCompanyStore!.company!
    const user = this.props.currentUserStore!.currentUser!
    this.createPayment = new CreatePayment(
      company.id,
      undefined,
      undefined,
      PaymentStatus.COMPLETED,
    )
    this.createPayment.setOfflinePaymentId(uuidv4())
    this.createPayment.setEmployeeId(user.id)
    this.state = {
      batches: [],
      isSaving: false,
      overrideTaxes: false,
      mappedTaxes: new Map<number, TaxModels.ITaxContract>(),
    }
  }

  componentDidMount = async () => {
    this.props.currentCompanyStore!.productStore!.getTaxGroups()

    const taxes = await taxApi.getTaxes()
    const mappedTaxes = new Map(taxes.map((tax) => [tax.id, tax]))

    this.setState({ mappedTaxes })
  }

  async loadBatches(productId: string) {
    const company = this.props.currentCompanyStore!.company!
    const locationId = this.createPayment.locationId
    if (!locationId) {
      this.setState({ batches: [] })
    } else {
      const batches = await API.getAvailableBatches(company.id, locationId, productId)
      this.setState({ batches })
    }
  }

  addProduct() {
    const { selectedProduct, selectedBatch, selectedTaxGroup } = this.state

    if (selectedProduct) {
      // If there is a tax override, apply it here
      const productTaxes = selectedTaxGroup
        ? selectedTaxGroup.taxLines.map((t) => ({
            ...t,
            name: this.state.mappedTaxes.get(t.taxId)!.name,
            id: t.taxId,
            exemptOverride: false,
          }))
        : selectedProduct.taxes
      const taxOverrideSelectedProduct = {
        ...selectedProduct,
        taxes: productTaxes,
      }

      this.createPayment.addProduct({
        product: taxOverrideSelectedProduct,
        pricePerInventorySubtracted: taxOverrideSelectedProduct.price || 0,
        inventoryToSubtract: 1,
        inventorySubtractIncrement: undefined,
        maximumQuantity: null,
        barcode: null,
        batchId: selectedBatch ? selectedBatch.id : undefined,
        batchNumber: selectedBatch ? selectedBatch.batchNumber : undefined,
      })
      this.setState({
        selectedProduct: undefined,
        selectedBatch: undefined,
      })
    }
  }

  applyTaxOverrideToCart() {
    const { selectedTaxGroup } = this.state
    if (selectedTaxGroup) {
      this.createPayment.paymentLines.forEach((paymentLine) => {
        paymentLine.product!.taxes = selectedTaxGroup.taxLines.map((t) => ({
          ...t,
          name: this.state.mappedTaxes.get(t.taxId)!.name,
          id: t.taxId,
          exemptOverride: false,
        }))
      })
    }
  }

  async submit() {
    if (!this.createPayment.locationId) {
      return message.error('Please select a location first')
    }
    if (!this.state.selectedPaymentTypeId) {
      return message.error('Please select a payment type')
    }

    try {
      const company = this.props.currentCompanyStore!.company!
      this.setState({ isSaving: true })

      // Clear all transactions
      const existingTransactionLength = this.createPayment.transactions.length
      for (let i = 0; i < existingTransactionLength - 1; i++) {
        this.createPayment.removeTransactionAtIndex(0)
      }

      // Add transaction
      const transaction = new CreateTransaction(this.state.selectedPaymentTypeId)
      transaction.setAmount(this.createPayment.total)
      transaction.setCompleteDate(new Date())
      transaction.setStatus('completed' as TransactionStatus)
      if (
        this.state.selectedPaymentTypeId === PaymentTypeIds.merrco ||
        this.state.selectedPaymentTypeId === PaymentTypeIds.moneris
      ) {
        transaction.setTerminalInvoiceId('MANUAL')
      } else {
        transaction.setTerminalInvoiceId(null)
      }
      this.createPayment.addTransaction(transaction)

      // In case there are NaNs
      this.createPayment.paymentLines.forEach((paymentLine) => {
        if (isNaN(paymentLine.inventorySubtracted)) {
          paymentLine.setInventorySubtracted(0)
        }
      })

      // Set complete date
      this.createPayment.setCompleteDate(new Date())

      const createdPayment = await API.addPayment(
        company.id,
        this.createPayment.locationId,
        this.createPayment.getNetworkTransferObject(),
        true,
      )
      this.setState({ createdPayment: createdPayment, isSaving: false })
    } catch (error) {
      message.error(parseErrorMsg(error))
      this.setState({ isSaving: false })
    }
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const user = this.props.currentUserStore!.currentUser!
    const locations = this.props.currentCompanyStore!.locations || []
    const taxGroups = this.props.currentCompanyStore!.productStore?.taxGroups || []
    const { selectedProduct, selectedTaxGroup, batches, selectedBatch } = this.state

    const footer = this.state.createdPayment
      ? [
          <Button key='cancel' type='primary' onClick={() => this.props.onSubmit()}>
            Close
          </Button>,
        ]
      : [
          <Button key='cancel' onClick={() => this.props.onClose()}>
            Cancel
          </Button>,
          <Button
            key='submit'
            type='primary'
            loading={this.state.isSaving}
            disabled={
              !this.createPayment.locationId ||
              !this.state.selectedPaymentTypeId ||
              isEmpty(this.createPayment.paymentLines)
            }
            onClick={() => this.submit()}
          >
            Submit
          </Button>,
        ]

    return (
      <Modal
        title='Add manual sale'
        visible={true}
        onCancel={this.props.onClose}
        width={700}
        maskClosable={false}
        footer={footer}
      >
        {this.state.createdPayment ? (
          <div>
            <Alert
              type='success'
              showIcon
              style={{ marginBottom: 10 }}
              message='Sale successfully added'
              description='View receipt below'
            />
            <Receipt company={company} payment={this.state.createdPayment} />
          </div>
        ) : (
          <Descriptions bordered column={1}>
            <Descriptions.Item label='Employee'>{user.name}</Descriptions.Item>
            <Descriptions.Item label='Location'>
              <Select<number>
                placeholder='Select location (required)'
                value={this.createPayment.locationId || undefined}
                onChange={(value) => (this.createPayment.locationId = value)}
                style={{ width: '100%' }}
              >
                {locations.map((location) => (
                  <Select.Option key={location.id}>{location.name}</Select.Option>
                ))}
              </Select>
            </Descriptions.Item>
            <Descriptions.Item label='Products'>
              {this.createPayment.locationId ? (
                <>
                  <div className='table-responsive mb-2'>
                    <table className='table table-sm w-100'>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th style={{ width: 120 }}>$/unit</th>
                          <th style={{ width: 80 }}>Qty</th>
                          <th style={{ width: 30 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.createPayment.paymentLines.map((paymentLine, index) => {
                          return (
                            <tr key={index}>
                              <td>
                                <div>{paymentLine.productName}</div>
                                {!!paymentLine.batchNumber && (
                                  <div className='text-muted'>Lot #: {paymentLine.batchNumber}</div>
                                )}
                              </td>
                              <td style={{ width: 120 }}>
                                <CurrencyInput
                                  className='form-control'
                                  centValue={paymentLine.pricePerUnit}
                                  onChange={(value) =>
                                    paymentLine.setPricePerInventorySubtracted(value)
                                  }
                                />
                              </td>
                              <td style={{ width: 80 }}>
                                <input
                                  type='number'
                                  className='form-control'
                                  value={paymentLine.inventorySubtracted}
                                  onChange={(e) =>
                                    paymentLine.setInventorySubtracted(parseInt(e.target.value))
                                  }
                                />
                              </td>
                              <td style={{ width: 30 }}>
                                <Icon
                                  type='close-circle'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => this.createPayment.removePaymentLine(index)}
                                />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className='d-flex'>
                    <div className='mr-2' style={{ width: 250 }}>
                      <SellableProductAsyncInput
                        value={selectedProduct ? selectedProduct.id : undefined}
                        onSelect={(product) => {
                          this.setState({
                            selectedProduct: product,
                            batches: [],
                          })
                          if (product.isBatchTracked) {
                            this.loadBatches(product.id)
                          }
                        }}
                      />
                    </div>
                    {!!selectedProduct && selectedProduct.isBatchTracked && (
                      <Select<number>
                        style={{ width: 150, marginRight: 5 }}
                        placeholder='Lot #'
                        value={this.state.selectedBatch ? this.state.selectedBatch.id : undefined}
                        onChange={(batchId) => {
                          const matchingBatch = batches.find((batch) => batch.id === batchId)
                          this.setState({ selectedBatch: matchingBatch })
                        }}
                      >
                        {batches.map((batch) => (
                          <Select.Option key={batch.id} value={batch.id}>
                            {batch.batchNumber}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                    <Button
                      type='primary'
                      disabled={
                        !selectedProduct ||
                        (selectedProduct && selectedProduct.isBatchTracked && !selectedBatch)
                      }
                      onClick={() => {
                        this.addProduct()
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </>
              ) : (
                <Alert showIcon type='warning' message='Select a location first' />
              )}
            </Descriptions.Item>
            <Descriptions.Item label='Payment'>
              <Select<number>
                placeholder='Select payment type (required)'
                style={{ width: '100%' }}
                onChange={(type) => this.setState({ selectedPaymentTypeId: type })}
              >
                <Select.Option value={PaymentTypeIds.cash}>Cash</Select.Option>
                <Select.Option value={PaymentTypeIds.merrco}>Merrco</Select.Option>
                <Select.Option value={PaymentTypeIds.moneris}>Moneris</Select.Option>
                <Select.Option value={PaymentTypeIds.other}>Other</Select.Option>
                <Select.Option value={PaymentTypeIds.credit}>Credit (non-integrated)</Select.Option>
                <Select.Option value={PaymentTypeIds.debit}>Debit (non-integrated)</Select.Option>
              </Select>
            </Descriptions.Item>
            <Descriptions.Item label='Taxes'>
              {this.state.overrideTaxes && (
                <>
                  <Select<number>
                    placeholder='Select tax group'
                    style={{ width: '100%', marginBottom: 5 }}
                    value={selectedTaxGroup ? selectedTaxGroup.id : undefined}
                    onChange={(taxGroupId) => {
                      const taxGroup = taxGroups.find((t) => t.id === taxGroupId)!
                      this.setState(
                        {
                          selectedTaxGroup: taxGroup,
                        },
                        () => this.applyTaxOverrideToCart(),
                      )
                    }}
                  >
                    {taxGroups.map((taxGroup) => (
                      <Select.Option key={taxGroup.id} value={taxGroup.id}>
                        {taxGroup.name}
                      </Select.Option>
                    ))}
                  </Select>
                  <Alert
                    showIcon
                    type='warning'
                    style={{ marginBottom: 5 }}
                    message='This will change the default taxes for each product'
                    description={'This is useful for wholesale orders that may not require PST.'}
                  />
                </>
              )}
              {
                // POSListings don't seem to come with taxGroupIds, so we can't reverse this override once applied.
                !selectedTaxGroup && (
                  <div>
                    <Checkbox
                      checked={this.state.overrideTaxes}
                      onChange={() =>
                        this.setState({
                          overrideTaxes: !this.state.overrideTaxes,
                          selectedTaxGroup: undefined,
                        })
                      }
                    >
                      Override taxes
                    </Checkbox>
                  </div>
                )
              }
            </Descriptions.Item>
            <Descriptions.Item label='Notes'>
              <Input.TextArea
                rows={3}
                value={this.createPayment.notes || ''}
                onChange={(e) => this.createPayment.setNotes(e.target.value)}
              />
            </Descriptions.Item>
            <Descriptions.Item label='Subtotal'>
              <Currency centValue={this.createPayment.subTotal} />
            </Descriptions.Item>
            <Descriptions.Item label='Tax'>
              <Currency centValue={this.createPayment.tax} />
            </Descriptions.Item>
            <Descriptions.Item label='Total'>
              <Currency centValue={this.createPayment.total} />
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    )
  }
}
